import * as React from 'react';
import cx from 'classnames';
import { Button } from 'reactstrap';
import { LProps, withLocalization } from '../../utils/wrappers';
import { compose, Dispatch, bindActionCreators } from 'redux';
import { State } from '../../redux/state';
import { connect } from 'react-redux';
import ImageUploadTrigger from './ImageUploadTrigger';
import ImageInput, { PHOTO_ACCEPT, VIDEO_ACCEPT } from './ImageInput';
import {
  DataURLFromFile, clearFileInput, revokeDataURLFromFile, isHeicMimeType,
  prepareImageForMutation, ErrCouldNotCompressFile, isVideoFile
} from '../../utils/image';
import { isVideoUrl } from '../../utils/userApiHelper';
import ImageEditor from './ImageEditor';
import FormTextAspectRatio from './FormTextAspectRatio';
import { ImageStoreProps, withImages } from '../../redux/imageStore';
import actions from '../../redux/actions';
import { StyleState } from '../../utils/styles';

const isVideo = (img: string): boolean => isVideoUrl(img);

type MediaType = 'picture'|'video';

/** Keep story/action photos from being cover-cropped into short landscape strips. */
export const storyMediaImgStyle: React.CSSProperties = {
  display: 'inline-block',
  width: 'auto',
  maxWidth: '100%',
  height: 'auto',
  maxHeight: '70vh',
  objectFit: 'contain',
  objectPosition: 'center center'
};

interface ManualProps {
  id: string;
  disabled?: boolean;
  currentImage: string;
  prompt?: string;
  heading?: React.ReactNode;
  photoClassName?: string;
  readyPhotoClassName?: string;
  aspectRatio?: string;
  onDelete?: () => void;
  banner?: boolean;

}

interface Props extends ManualProps, ImageStoreProps {
  postedImage: string;
  showToast: (message: string) => void;
}

interface ImageUploaderState {
  dataURLFromFile: DataURLFromFile;
  editing: boolean;
  type: MediaType;
}
const initialState: ImageUploaderState = {
  dataURLFromFile: null,
  editing: false,
  type: 'picture'
};
const wasImagePostedOrCleared = (nextProps: Props, actualProps: Props) =>
  nextProps.postedImage !== actualProps.postedImage;

class ImageUploader extends React.Component<LProps<Props>, ImageUploaderState> {
  public state = initialState;

  private open(kind: MediaType = 'picture') {
    if (this.props.disabled) return;
    const inputID = kind === 'video' ? `${this.props.id}-video` : this.props.id;
    const el = document.getElementById(inputID);
    if (el) el.click();
  }

  private delete() {
    const { onDelete } = this.props;
    const { dataURLFromFile } = this.state;
    if (dataURLFromFile) {
      this.clearDataURL();
    } else if (onDelete) {
      onDelete();
    }
  }

  // Once the photo has been uploaded, display the uploaded photo,
  // clear the file input, and dataURL preview so it is not present
  // once the form has been saved.
  public async componentWillReceiveProps(nextProps: LProps<Props>) {
    if (wasImagePostedOrCleared(nextProps, this.props)) {
      this.clearDataURL();
    }
  }

  public componentWillUnmount() {
    const { delImage, id } = this.props;
    delImage(id);
  }

  private clearDataURL() {
    const { id, delImage } = this.props;
    revokeDataURLFromFile(this.state.dataURLFromFile);
    this.setState({ dataURLFromFile: null, editing: false, type: 'picture' });
    delImage(id);
    clearFileInput(id);
    clearFileInput(`${id}-video`);
  }

  private async setEditedDataURLFromFile(dataURLFromFile: DataURLFromFile, initialize: boolean = false) {
    const { id, setImage, showToast, t } = this.props;
    let type: MediaType = 'picture';
    let editing = this.state.editing;
    let readyImage = dataURLFromFile;
    const mime = (dataURLFromFile.mimeType || '').toLowerCase();
    const pickedVideo = mime.indexOf('video') >= 0 ||
      (dataURLFromFile.file ? isVideoFile(dataURLFromFile.file) : false) ||
      /\.(mp4|mov|m4v|avi|webm|mpeg|mpg)$/i.test(dataURLFromFile.filename || '');
    try {
      if (initialize && pickedVideo) {
        readyImage = dataURLFromFile;
        type = 'video';
        editing = false;
      } else if (initialize && isHeicMimeType(dataURLFromFile.mimeType, dataURLFromFile.filename) && dataURLFromFile.file) {
        // Upload HEIC as-is (no crop/compress pipeline — Chrome often cannot decode HEIC).
        readyImage = dataURLFromFile;
        editing = false;
      } else if (initialize) {
        // Compress locally (JPEG ≤ ~1MB), then open crop UI with a near-full
        // percent crop so users can adjust or tap Use full photo.
        readyImage = await prepareImageForMutation(dataURLFromFile);
        editing = true;
      }
    } catch (err) {
      const errMsg = (err instanceof Error ? err.message : String(err)) || '';
      const isVideoTooLarge = /video must be 50mb/i.test(errMsg);
      const isTooLarge = err === ErrCouldNotCompressFile ||
        errMsg.toLowerCase().indexOf('compress') >= 0;
      const message = isVideoTooLarge
        ? errMsg
        : isTooLarge
        ? t('This photo is too large to upload. Try a smaller image.')
        : (errMsg || t('Could not process that image. Try JPEG or PNG under 10MB.'));
      showToast(message);
      this.clearDataURL();
      return;
    }
    if (!readyImage) editing = false;
    this.setState({ dataURLFromFile: readyImage, editing, type });
    setImage(id, readyImage);
    clearFileInput(id);
  }

  public render() {
    const { dataURLFromFile, editing, type } = this.state;
    const {
      id, t, currentImage, prompt, heading, onDelete, aspectRatio,
      readyPhotoClassName = '', photoClassName = '', disabled = false
    } = this.props;
    const anyImage = Boolean(dataURLFromFile || currentImage);
    const showingVideo = type === 'video' || Boolean(currentImage && isVideo(currentImage));
    return (
      <div
        className={'cc__image_uploader'}
      >
        <ImageInput
          id={id}
          accept={PHOTO_ACCEPT}
          ariaLabel={t('Photo upload')}
          onChange={(newFile) => this.setEditedDataURLFromFile(newFile, true)}
          onError={(err) => this.props.showToast(err.message)}
        />
        <ImageInput
          id={`${id}-video`}
          accept={VIDEO_ACCEPT}
          ariaLabel={t('Video upload')}
          onChange={(newFile) => this.setEditedDataURLFromFile(newFile, true)}
          onError={(err) => this.props.showToast(err.message)}
        />
        {!anyImage &&
          <ImageUploadTrigger
            fileInputID={id}
            videoInputID={`${id}-video`}
            prompt={prompt}
            heading={heading}
            disabled={disabled}
          />
        }
        {/* Keep ImageEditor outside photo preview classes — contain/max-height on
            .cc__story__photo img breaks ReactCrop hit-testing (selection vanishes). */}
        {anyImage && !showingVideo && editing && dataURLFromFile &&
          <ImageEditor
            dataURLFromFile={dataURLFromFile}
            onChange={(update) => this.setEditedDataURLFromFile(update)}
            onCropApplied={() => this.setState({ editing: false })}
          />
        }
        <div className={cx(photoClassName, { [readyPhotoClassName]: !editing && anyImage })}>
          {anyImage && !showingVideo && !editing &&
            (() => {
              if (dataURLFromFile) {
                  if (isHeicMimeType(dataURLFromFile.mimeType, dataURLFromFile.filename) && dataURLFromFile.file) {
                    return (
                      <div className="cc__image_uploader__heic_preview">
                        <img
                          src={dataURLFromFile.dataURL}
                          alt={dataURLFromFile.filename || t('preview')}
                          style={storyMediaImgStyle}
                          width={dataURLFromFile.width || undefined}
                          height={dataURLFromFile.height || undefined}
                          onError={(e) => {
                            const el = e.currentTarget;
                            el.style.display = 'none';
                            if (el.nextElementSibling) {
                              (el.nextElementSibling as HTMLElement).style.display = 'block';
                            }
                          }}
                        />
                        <div style={{ display: 'none', padding: '1rem', textAlign: 'center' }}>
                          {dataURLFromFile.filename || t('HEIC photo ready to upload')}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <img
                      src={dataURLFromFile.dataURL}
                      alt={dataURLFromFile.filename || t('preview')}
                      style={storyMediaImgStyle}
                      width={dataURLFromFile.width || undefined}
                      height={dataURLFromFile.height || undefined}
                    />
                  );
                } else if (currentImage) {
                  if (!isVideo(currentImage)) {
                    return (<img src={currentImage} alt={t('image')} style={storyMediaImgStyle} />);
                  } else {
                    return (<video controls src={currentImage} title={t('preview')} style={storyMediaImgStyle} />);
                  }
                }
              }
            )()
          }
          {anyImage && showingVideo &&
            <video controls src={(dataURLFromFile && dataURLFromFile.dataURL) || currentImage} title={(dataURLFromFile && dataURLFromFile.filename) || t('preview')} />
          }
        </div>
        {anyImage &&
          <div className={'cc__image_loader__controls'}>
            <Button
              size="sm"
              color="primary"
              title={t('Add') + ' ' + t(type)}
              aria-label={t('Add') + ' ' + t(type)}
              disabled={disabled}
              onClick={() => this.open(type)}
            ><i className={type === 'video' ? 'fa fa-video-camera' : 'fa fa-picture-o'} /></Button>
            {dataURLFromFile  && type == 'picture' &&
              <Button
                outline={!editing}
                aria-label={editing ? t('Done editing photo') : t('Crop photo (optional)')}
                title={editing ? t('Done editing photo') : t('Crop photo (optional)')}
                size="sm"
                color="primary"
                onClick={() => this.setState({ editing: !this.state.editing })}
              ><i className={editing ? 'fa fa-check' : 'fa fa-pencil'} /></Button>
            }
            {(dataURLFromFile || onDelete) &&
              <Button
                size="sm"
                title={t('Delete') + ' ' + t(type)}
                aria-label={t('Delete') + ' ' + t(type)}
                color="danger"
                onClick={() => this.delete()}
              ><i className="fa fa-times" /></Button>}
          </div>
        }
        {aspectRatio && <FormTextAspectRatio idealRatio={aspectRatio} />}
      </div>
    );
  }
}

const mapStateToProps = (state: State, ownProps: Partial<ManualProps>): Partial<Props> => ({
  postedImage: state.postedImages[ownProps.id]
});
const mapDispatchToProps = (dispatch: Dispatch, ownProps: Partial<ManualProps>) => bindActionCreators({
  showToast: (message: string) => actions.toast({ style: StyleState.Warning, message }, 5000)
}, dispatch);

export default compose(
  withLocalization('imageUploader'),
  connect(mapStateToProps, mapDispatchToProps),
  withImages([])
)(ImageUploader) as React.SFC<ManualProps>;
